/**
 * star_look — WHAT each kind of star looks like and how it behaves (the natural object layer, 2026-10-07). PURE: numbers
 * in, numbers out; no three, no DOM. `star_view.ts` and `space_sun.ts` draw what this file says; `verify:natural-stars`
 * pins it.
 *
 * WHERE THE NUMBERS COME FROM (docs/research/2026-10-07-space-objects/impl-S.md has the sources):
 *  · COLOUR is a blackbody at the star's effective temperature (space_look.kelvinToRgb) times what its molecules do to
 *    it. A red dwarf is ORANGE, not crimson (Harre & Heller 2021: M0–M9 ≈ sRGB 1 / 0.64 / 0.30–0.35 — titanium oxide
 *    eats the blue; "there are no yellow, green, cyan or purple stars"); a carbon star is the reddest thing in the sky
 *    (C₂ / CN take what blue is left); a brown dwarf is a dull magenta (sodium and potassium take the green); a hot
 *    star is blue-WHITE, never blue (the catalog's own caution, §4).
 *  · LIMB DARKENING is stronger the cooler the star (Claret's tables: the linear coefficient climbs from ~0.3 for an O
 *    star to ~0.85 for an M dwarf); a giant's edge is soft (an extended atmosphere), a Wolf–Rayet's is its wind.
 *  · GRANULES are as wide as the pressure scale height allows, ∝ T/g (Freytag; Schwarzschild 1975): fine on a dwarf, a
 *    FEW giant cells on a red supergiant (Betelgeuse: cells up to 60 % of the disc, hot spots +800 K).
 *  · SPOTS cover 20–40 % of young K and M dwarfs and T Tauri stars; none on A / B / O stars (no convective skin).
 *  · VARIABLES. A Cepheid rises fast and falls slowly (δ Cep: 28 % of the cycle rising — "a bent saw-tooth"), hotter at
 *    maximum. A Mira's visible light swings far more than its total (the radius moves ~20 %, the temperature < 10 %, and
 *    the visible share of a 2,500–3,300 K blackbody moves with it), reddest at minimum. An LBV's S Doradus cycle trades
 *    temperature for size at constant total light: hot and small in quiescence, cool, large and visually BRIGHTER in
 *    outburst. A flare (Davenport 2014: a fast rise, then a fast and a slow exponential) is a white patch and a blue kick.
 *
 * TIME IS THE WALL CLOCK. Everything periodic is a function of `nowS` (Date.now() / 1000) and the star's seed — every
 * client sees the same phase. A wall-clock second count does not fit a float32 (128 s steps): phases are reduced HERE,
 * in doubles, and only angles and fractions ever reach a uniform.
 */
import { kelvinToRgb, discOverlapArea } from '../space_look.js';
import { STELLAR_REACH, type StarKind, type SystemConfig } from './natural_types.js';

export type Rgb = [number, number, number];
export type V3 = [number, number, number];

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const TAU = Math.PI * 2;
const frac = (v: number): number => v - Math.floor(v);

/** fnv + murmur3's fmix32 (THE HASH LAW: bare FNV-1a is arithmetic in its last character). */
export function starHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return h >>> 0;
}
export const starHash01 = (s: string): number => starHash(s) / 4294967296;

// ── the table ───────────────────────────────────────────────────────────────────────────────────────────────────
export type VaryKind =
  | 'steady' | 'active' | 'flare' | 'cepheid' | 'mira' | 'semiregular' | 'sdor' | 'ttauri' | 'herbig' | 'protostar'
  | 'be' | 'pulsar' | 'magnetar';

export interface StarLook {
  /** Effective temperature (K): the colour's source and the order the harness checks. */
  kelvin: number;
  /** What the atmosphere's molecules do to a blackbody of that temperature (an sRGB multiplier; 1 = a blackbody). */
  tint: Rgb;
  /** A whole colour where no blackbody says it (sRGB, max channel 1): a brown dwarf's magenta. */
  srgb?: Rgb;
  /** The DRAWN photosphere: a fraction of the layout's footprint … */
  size: number;
  /** … or metres, for what is far smaller than any footprint (a white dwarf, a neutron star). */
  sizeM?: number;
  /** Limb darkening I(μ) = a + bμ − cμ², b = 1 − a + c: `a` is the brightness left at the very edge. */
  limbA: number; limbC: number;
  /** How much redder the limb is than the centre (0..1; the limb shows cooler, higher gas). */
  limbRed: number;
  /** The edge's softness in disc radii (a dwarf's is sharp; a giant's atmosphere and a Wolf–Rayet's wind are not). */
  fuzz: number;
  /** Granulation: cells per quad half-width at two scales, their contrast, and how fast they boil (× the Sun's). */
  gran: [scale1: number, scale2: number, contrast1: number, contrast2: number, speed: number];
  /** Spots: the share of the disc under them, how dark, the pattern's scale and its stretch along the equator (bands). */
  spots: [cover: number, depth: number, scale: number, banding: number];
  /** The chromosphere's rim: colour (linear), gain and width (disc radii). */
  rim: [r: number, g: number, b: number, gain: number, width: number];
  /** × the corona's gain, and its radial power (the Sun's falls as r^−2.5; a dense wind hugs the star). */
  corona: number; coronaPow: number;
  /** × SUN_DISC_EMISSION — the photosphere's brightness on screen (a cool surface is a dimmer one). */
  emission: number;
  /** × the key light (floored at KEY_LIGHT_FLOOR for play: a hull must stay readable under a brown dwarf). */
  light: number;
  /** Polar radius ÷ equatorial radius (a Be star spins near break-up) and how much brighter its poles are (von Zeipel). */
  oblate: number; gravDark: number;
  /** One turn of the surface in seconds of wall clock (spots drift across the disc); 0 = no visible turn. */
  spinS: number;
  /** Relative mass (a pair's barycentre). */
  mass: number;
  vary: VaryKind;
}

const SUN_GRAN: StarLook['gran'] = [13, 37, 0.07, 0.05, 1];
const SUN_RIM: StarLook['rim'] = [1.0, 0.42, 0.22, 0.12, 0.03];
const HOT_RIM: StarLook['rim'] = [0.7, 0.8, 1.0, 0.06, 0.03];
const NO_GRAN: StarLook['gran'] = [13, 37, 0, 0, 1];

/**
 * Every kind. Temperatures are mid-class values; `size` is the coordinator's brief (a supergiant ~1.15–1.2 footprints,
 * a giant ~1, the main sequence 0.5–0.9 rising with mass, an M dwarf small, a brown dwarf smaller, a white dwarf a few
 * km, a neutron star a point). `emission` falls with temperature (a photosphere's surface brightness is ∝ T⁴ — the
 * game keeps a tenth of that range so a cool disc is dimmer yet still a star).
 */
export const STAR_LOOK: Readonly<Record<StarKind, StarLook>> = {
  // ── the main sequence ──
  m_dwarf: { kelvin: 3300, tint: [1, 0.89, 0.63], size: 0.5, limbA: 0.16, limbC: 0.2, limbRed: 0.3, fuzz: 0.004, gran: [24, 61, 0.05, 0.04, 1.6], spots: [0.2, 0.42, 4.4, 1], rim: [1.0, 0.36, 0.2, 0.2, 0.035], corona: 0.85, coronaPow: -2.5, emission: 0.5, light: 0.62, oblate: 1, gravDark: 0, spinS: 520, mass: 0.3, vary: 'flare' },
  k_dwarf: { kelvin: 4600, tint: [1, 0.93, 0.8], size: 0.62, limbA: 0.23, limbC: 0.22, limbRed: 0.28, fuzz: 0.004, gran: [17, 47, 0.07, 0.05, 1.2], spots: [0.08, 0.42, 3.8, 1], rim: [1.0, 0.4, 0.22, 0.15, 0.03], corona: 1.2, coronaPow: -2.5, emission: 0.64, light: 0.86, oblate: 1, gravDark: 0, spinS: 700, mass: 0.7, vary: 'active' },
  g_dwarf: { kelvin: 5800, tint: [1, 1, 1], size: 0.66, limbA: 0.3, limbC: 0.24, limbRed: 0.22, fuzz: 0.004, gran: SUN_GRAN, spots: [0.02, 0.55, 2.3, 1], rim: SUN_RIM, corona: 1, coronaPow: -2.5, emission: 1, light: 1, oblate: 1, gravDark: 0, spinS: 900, mass: 1, vary: 'steady' },
  f_dwarf: { kelvin: 6600, tint: [1, 1, 1], size: 0.74, limbA: 0.36, limbC: 0.22, limbRed: 0.16, fuzz: 0.004, gran: [10, 29, 0.05, 0.035, 0.9], spots: [0.004, 0.45, 2.3, 1], rim: [1.0, 0.5, 0.36, 0.08, 0.028], corona: 0.7, coronaPow: -2.5, emission: 1.15, light: 1, oblate: 0.985, gravDark: 0.05, spinS: 420, mass: 1.3, vary: 'steady' },
  a_star: { kelvin: 8600, tint: [1, 1, 1], size: 0.84, limbA: 0.46, limbC: 0.2, limbRed: 0.1, fuzz: 0.004, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: HOT_RIM, corona: 0.15, coronaPow: -2.5, emission: 1.35, light: 1, oblate: 0.93, gravDark: 0.22, spinS: 260, mass: 2, vary: 'steady' },
  ob_star: { kelvin: 30000, tint: [1, 1, 1], size: 0.86, limbA: 0.6, limbC: 0.14, limbRed: 0.05, fuzz: 0.006, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: [0.66, 0.78, 1.0, 0.16, 0.06], corona: 0.6, coronaPow: -3, emission: 1.7, light: 1, oblate: 0.96, gravDark: 0.12, spinS: 300, mass: 20, vary: 'steady' },
  // ── evolved ──
  red_giant: { kelvin: 4200, tint: [1, 0.95, 0.86], size: 1, limbA: 0.14, limbC: 0.2, limbRed: 0.34, fuzz: 0.012, gran: [5, 14, 0.14, 0.07, 0.4], spots: [0.03, 0.4, 1.6, 1], rim: [1.0, 0.36, 0.2, 0.1, 0.05], corona: 0.35, coronaPow: -3, emission: 0.6, light: 0.92, oblate: 1, gravDark: 0, spinS: 0, mass: 1.5, vary: 'steady' },
  agb: { kelvin: 3100, tint: [1, 0.88, 0.66], size: 1.1, limbA: 0.1, limbC: 0.16, limbRed: 0.4, fuzz: 0.03, gran: [3.2, 9, 0.22, 0.1, 0.3], spots: [0.04, 0.4, 1.3, 1], rim: [1.0, 0.3, 0.14, 0.14, 0.06], corona: 0.5, coronaPow: -3.4, emission: 0.42, light: 0.85, oblate: 1, gravDark: 0, spinS: 0, mass: 1.5, vary: 'semiregular' },
  mira: { kelvin: 2950, tint: [1, 0.86, 0.62], size: 1.02, limbA: 0.1, limbC: 0.16, limbRed: 0.42, fuzz: 0.035, gran: [3, 8.5, 0.22, 0.1, 0.3], spots: [0.04, 0.4, 1.3, 1], rim: [1.0, 0.3, 0.14, 0.16, 0.06], corona: 0.5, coronaPow: -3.4, emission: 0.4, light: 0.8, oblate: 1, gravDark: 0, spinS: 0, mass: 1.2, vary: 'mira' },
  carbon_star: { kelvin: 2800, tint: [1, 0.56, 0.24], size: 1.05, limbA: 0.09, limbC: 0.15, limbRed: 0.45, fuzz: 0.035, gran: [3, 8, 0.2, 0.1, 0.3], spots: [0.05, 0.45, 1.3, 1], rim: [1.0, 0.2, 0.06, 0.16, 0.06], corona: 0.6, coronaPow: -3.4, emission: 0.36, light: 0.72, oblate: 1, gravDark: 0, spinS: 0, mass: 1.5, vary: 'semiregular' },
  post_agb: { kelvin: 6400, tint: [1, 0.98, 0.94], size: 0.5, limbA: 0.3, limbC: 0.22, limbRed: 0.2, fuzz: 0.012, gran: [7, 20, 0.06, 0.04, 0.6], spots: [0, 0, 2.3, 1], rim: [1.0, 0.6, 0.4, 0.1, 0.05], corona: 0.4, coronaPow: -3, emission: 0.95, light: 0.78, oblate: 1, gravDark: 0, spinS: 0, mass: 0.6, vary: 'steady' },
  cepheid: { kelvin: 5900, tint: [1, 1, 1], size: 0.92, limbA: 0.28, limbC: 0.24, limbRed: 0.24, fuzz: 0.008, gran: [6, 18, 0.08, 0.05, 0.5], spots: [0, 0, 2.3, 1], rim: [1.0, 0.45, 0.25, 0.1, 0.04], corona: 0.3, coronaPow: -3, emission: 0.95, light: 0.82, oblate: 1, gravDark: 0, spinS: 0, mass: 6, vary: 'cepheid' },
  red_supergiant: { kelvin: 3550, tint: [1, 0.88, 0.66], size: 1.16, limbA: 0.1, limbC: 0.14, limbRed: 0.4, fuzz: 0.04, gran: [1.7, 5.2, 0.36, 0.16, 0.22], spots: [0.02, 0.3, 1, 1], rim: [1.0, 0.3, 0.14, 0.16, 0.06], corona: 0.6, coronaPow: -3.6, emission: 0.5, light: 0.95, oblate: 1, gravDark: 0, spinS: 0, mass: 15, vary: 'semiregular' },
  blue_supergiant: { kelvin: 13500, tint: [1, 1, 1], size: 1.04, limbA: 0.5, limbC: 0.16, limbRed: 0.06, fuzz: 0.008, gran: [9, 26, 0.02, 0.012, 0.5], spots: [0, 0, 2.3, 1], rim: [0.7, 0.8, 1.0, 0.12, 0.06], corona: 0.5, coronaPow: -3, emission: 1.5, light: 1, oblate: 1, gravDark: 0, spinS: 0, mass: 20, vary: 'steady' },
  lbv: { kelvin: 17000, tint: [1, 1, 1], size: 1, limbA: 0.5, limbC: 0.16, limbRed: 0.08, fuzz: 0.02, gran: [6, 17, 0.04, 0.02, 0.5], spots: [0, 0, 2.3, 1], rim: [0.8, 0.8, 1.0, 0.2, 0.06], corona: 0.9, coronaPow: -3.2, emission: 1.4, light: 0.9, oblate: 1, gravDark: 0, spinS: 0, mass: 50, vary: 'sdor' },
  wolf_rayet: { kelvin: 40000, tint: [1, 1, 1], size: 0.4, limbA: 0.62, limbC: 0.1, limbRed: 0.04, fuzz: 0.045, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: [0.72, 0.8, 1.0, 0.4, 0.06], corona: 2.6, coronaPow: -3.6, emission: 1.9, light: 0.95, oblate: 1, gravDark: 0, spinS: 0, mass: 12, vary: 'steady' },
  be_star: { kelvin: 20000, tint: [1, 1, 1], size: 0.6, limbA: 0.56, limbC: 0.14, limbRed: 0.05, fuzz: 0.005, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: [1.0, 0.5, 0.6, 0.12, 0.05], corona: 0.4, coronaPow: -3, emission: 1.5, light: 1, oblate: 0.74, gravDark: 0.42, spinS: 110, mass: 8, vary: 'be' },
  // ── young ──
  protostar: { kelvin: 3600, tint: [1, 0.66, 0.34], size: 0.5, limbA: 0.14, limbC: 0.18, limbRed: 0.4, fuzz: 0.03, gran: [8, 22, 0.1, 0.06, 0.8], spots: [0.18, 0.45, 3.2, 1], rim: [1.0, 0.3, 0.12, 0.2, 0.12], corona: 0.3, coronaPow: -3, emission: 0.25, light: 0.36, oblate: 1, gravDark: 0, spinS: 400, mass: 0.6, vary: 'protostar' },
  t_tauri: { kelvin: 4100, tint: [1, 0.93, 0.8], size: 0.86, limbA: 0.2, limbC: 0.2, limbRed: 0.3, fuzz: 0.006, gran: [12, 34, 0.08, 0.05, 1.2], spots: [0.3, 0.5, 3.1, 1], rim: [1.0, 0.36, 0.3, 0.22, 0.04], corona: 1.6, coronaPow: -2.5, emission: 0.5, light: 0.7, oblate: 1, gravDark: 0, spinS: 300, mass: 0.8, vary: 'ttauri' },
  herbig: { kelvin: 9200, tint: [1, 1, 1], size: 0.8, limbA: 0.46, limbC: 0.2, limbRed: 0.1, fuzz: 0.005, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: HOT_RIM, corona: 0.6, coronaPow: -2.8, emission: 1.2, light: 0.95, oblate: 0.95, gravDark: 0.14, spinS: 240, mass: 2.5, vary: 'herbig' },
  // ── substellar and compact ──
  brown_dwarf: { kelvin: 1700, tint: [1, 1, 1], srgb: [1, 0.36, 0.4], size: 0.42, limbA: 0.2, limbC: 0.2, limbRed: 0.2, fuzz: 0.006, gran: [10, 26, 0.05, 0.04, 0.6], spots: [0.5, 0.5, 2.2, 6], rim: [1.0, 0.3, 0.4, 0.05, 0.04], corona: 0.08, coronaPow: -3, emission: 0.07, light: 0.3, oblate: 0.94, gravDark: 0, spinS: 90, mass: 0.05, vary: 'steady' },
  white_dwarf: { kelvin: 16000, tint: [1, 1, 1], size: 0.06, sizeM: 6_000, limbA: 0.5, limbC: 0.18, limbRed: 0.05, fuzz: 0.004, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: HOT_RIM, corona: 0.25, coronaPow: -3, emission: 2.2, light: 0.5, oblate: 1, gravDark: 0, spinS: 0, mass: 0.8, vary: 'steady' },
  neutron_star: { kelvin: 40000, tint: [1, 1, 1], size: 0.02, sizeM: 1_200, limbA: 0.7, limbC: 0.1, limbRed: 0, fuzz: 0.004, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: HOT_RIM, corona: 0.6, coronaPow: -3, emission: 2.6, light: 0.32, oblate: 1, gravDark: 0, spinS: 0, mass: 1.4, vary: 'steady' },
  pulsar: { kelvin: 40000, tint: [1, 1, 1], size: 0.02, sizeM: 1_200, limbA: 0.7, limbC: 0.1, limbRed: 0, fuzz: 0.004, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: HOT_RIM, corona: 0.8, coronaPow: -3, emission: 2.6, light: 0.34, oblate: 1, gravDark: 0, spinS: 0, mass: 1.4, vary: 'pulsar' },
  magnetar: { kelvin: 40000, tint: [1, 1, 1], size: 0.02, sizeM: 1_200, limbA: 0.7, limbC: 0.1, limbRed: 0, fuzz: 0.004, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: HOT_RIM, corona: 1, coronaPow: -3, emission: 2.6, light: 0.32, oblate: 1, gravDark: 0, spinS: 0, mass: 1.6, vary: 'magnetar' },
  // a black hole has no surface: no light of its own (what is seen is what falls in — star_view draws that)
  black_hole: { kelvin: 5000, tint: [1, 1, 1], size: 0.02, sizeM: 2_400, limbA: 1, limbC: 0, limbRed: 0, fuzz: 0.004, gran: NO_GRAN, spots: [0, 0, 2.3, 1], rim: [0, 0, 0, 0, 0.03], corona: 0, coronaPow: -2.5, emission: 0, light: 0.3, oblate: 1, gravDark: 0, spinS: 0, mass: 8, vary: 'steady' },
};
export const STAR_KINDS = Object.keys(STAR_LOOK) as StarKind[];

/** The key light never falls below this (a hull must stay readable under the dimmest star), nor a steady star above 1. */
export const KEY_LIGHT_FLOOR = 0.3;
/** A transient (a flare, a nova, a magnetar's burst) may lift the key this far — past it lit hulls bloom into haze. */
export const KEY_LIGHT_BURST = 1.5;
/** How far the KEY light's colour moves toward white (the eye adapts to its illuminant — CIECAM02's degree of
 *  adaptation is ~0.65 in a dark surround); the DISC keeps the star's true colour. */
export const KEY_ADAPT = 0.6;

// ── colour ──────────────────────────────────────────────────────────────────────────────────────────────────────
const toLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const toSrgb = (c: number): number => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
const norm1 = (c: Rgb): Rgb => { const m = Math.max(c[0], c[1], c[2], 1e-6); return [c[0] / m, c[1] / m, c[2] / m]; };

/** The star's colour as an eye in space sees its unsaturated disc: sRGB, brightest channel 1. */
export function starSrgb(kind: StarKind, kelvin?: number): Rgb {
  const L = STAR_LOOK[kind];
  if (L.srgb) return [L.srgb[0], L.srgb[1], L.srgb[2]];
  const k = Number.isFinite(kelvin) ? (kelvin as number) : L.kelvin;
  const bb = kelvinToRgb(clamp(k, 1000, 40000));
  // a tint belongs to the star's own temperature: a Mira at maximum has burnt off some of its oxide (the blue comes back)
  const w = L.kelvin > 0 ? clamp(1 - (k - L.kelvin) / 1500, 0, 1.4) : 1;
  return norm1([bb[0] * lerp(1, L.tint[0], w), bb[1] * lerp(1, L.tint[1], w), bb[2] * lerp(1, L.tint[2], w)]);
}
/** The same in LINEAR rgb (what a uniform and a light take), brightest channel 1. */
export function starRgb(kind: StarKind, kelvin?: number): Rgb {
  const s = starSrgb(kind, kelvin);
  return norm1([toLinear(s[0]), toLinear(s[1]), toLinear(s[2])]);
}
/** The key light's colour for a disc colour (linear): KEY_ADAPT of the way to white, brightest channel 1. */
export function keyRgb(disc: Rgb, adapt = KEY_ADAPT): Rgb {
  return norm1([lerp(disc[0], 1, adapt), lerp(disc[1], 1, adapt), lerp(disc[2], 1, adapt)]);
}
/** The galaxy chart's dot (sRGB hex): the star's colour, dimmed for what gives little light. */
export function starChartHex(kind: StarKind): number {
  const s = starSrgb(kind), L = STAR_LOOK[kind];
  const g = kind === 'black_hole' ? 0.35 : clamp(0.55 + 0.45 * Math.sqrt(L.emission), 0.6, 1);
  const c = kind === 'black_hole' ? [0.5, 0.42, 0.6] : s;
  return (Math.round(clamp(c[0] * g, 0, 1) * 255) << 16) | (Math.round(clamp(c[1] * g, 0, 1) * 255) << 8) | Math.round(clamp(c[2] * g, 0, 1) * 255);
}
export const srgbOf = (lin: Rgb): Rgb => [toSrgb(lin[0]), toSrgb(lin[1]), toSrgb(lin[2])];

// ── size and surface ────────────────────────────────────────────────────────────────────────────────────────────
/** The drawn photosphere radius (m) of a kind standing alone on a layout of this footprint. */
export function starRadiusM(kind: StarKind, footprintM: number): number {
  const L = STAR_LOOK[kind];
  return L.sizeM !== undefined ? Math.min(L.sizeM, footprintM * L.size) : footprintM * L.size;
}
/** A kind's surface as the sun rig's shader takes it (structurally space_sun.SunSurface — this file stays three-free). */
export interface StarSurface {
  limb: [number, number, number]; limbRed: [number, number, number]; fuzz: number;
  gran: [number, number, number, number]; granSpeed: [number, number];
  spot: [number, number, number, number]; spotScale: [number, number, number]; sphere: boolean;
  rim: [number, number, number, number]; rimWidth: number; coronaPow: number; coronaRays: number;
  patchRgb: [number, number, number]; patchWidth: number;
}
/** The Sun's own boil rates (space_sun's defaults): a kind's `gran[4]` scales them. */
const GRAN_RATE: readonly [number, number] = [0.035, 0.08];
/**
 * How strongly a kind's halo breaks into streamers. The Sun's corona is magnetic (0.9: rays and helmets); a giant's or a
 * hot star's halo is its WIND — smooth, at most clumpy.
 */
const CORONA_RAYS: Readonly<Partial<Record<StarKind, number>>> = {
  m_dwarf: 0.7, t_tauri: 0.8, k_dwarf: 0.8, red_giant: 0.3, agb: 0.22, mira: 0.22, carbon_star: 0.22, red_supergiant: 0.25, post_agb: 0.3, cepheid: 0.4,
  ob_star: 0.4, blue_supergiant: 0.4, lbv: 0.45, wolf_rayet: 0.4, be_star: 0.3, a_star: 0.4, herbig: 0.5, protostar: 0.3,
  brown_dwarf: 0.3, white_dwarf: 0.25, neutron_star: 0.5, pulsar: 0.5, magnetar: 0.6, black_hole: 0,
};
/** How soft a spot's edge is (the field's span from first shade to full dark): the Sun's are crisp, a cool dwarf's huge groups are not. */
const SPOT_SOFT = 0.24;
export function surfaceOf(kind: StarKind): StarSurface {
  const L = STAR_LOOK[kind];
  let [lo, hi] = spotThresholds(L.spots[0]);
  if (L.spots[0] > 0.03) { const mid = (lo + hi) / 2, soft = L.spots[0] >= 0.2 ? SPOT_SOFT * 1.6 : SPOT_SOFT; lo = mid - soft / 2; hi = mid + soft / 2; }
  const band = Math.sqrt(Math.max(1, L.spots[3]));   // bands: long along the equator, short across it
  return {
    limb: limbCoeffs(kind), limbRed: [0, L.limbRed * 0.5, L.limbRed], fuzz: L.fuzz,
    gran: [L.gran[0], L.gran[1], L.gran[2], L.gran[3]], granSpeed: [GRAN_RATE[0] * L.gran[4], GRAN_RATE[1] * L.gran[4]],
    spot: [lo, hi, L.spots[1], 0.004], spotScale: [L.spots[2] / band, L.spots[2] * band, L.spots[2] / band], sphere: true,
    rim: [L.rim[0], L.rim[1], L.rim[2], L.rim[3]], rimWidth: L.rim[4], coronaPow: L.coronaPow, coronaRays: CORONA_RAYS[kind] ?? 0.9,
    patchRgb: [0.8, 0.88, 1], patchWidth: 0.2,
  };
}
/**
 * The lens glare a star of this surface brightness and apparent size earns (× the rig's). The streak and the starburst
 * belong to a bright POINT: a dim photosphere flares little (a brown dwarf: nothing), and a disc that fills a tenth of
 * the view is a surface, not a point — its glare falls to a fifth so the spikes do not cross its own face.
 */
/** A derived star's glare against the Commons Reach's (the spikes are the camera's, not the star's: kept modest), and the emission past which it grows no more. */
export const GLARE_BASE = 0.62;
export const GLARE_EMISSION_CAP = 3;
export function glareGain(emission: number, angR: number): number {
  if (!(emission > 0)) return 0;
  const t = clamp((angR - 0.02) / (0.2 - 0.02), 0, 1), big = t * t * (3 - 2 * t);
  return clamp(emission, 0, GLARE_EMISSION_CAP) * GLARE_BASE * lerp(1, 0.3, big);
}
/** The radius (m) that is `px` pixels on screen at `dist`: what a point of light is drawn no smaller than. */
export function pixelFloorM(dist: number, fovYRad: number, heightPx: number, px: number): number {
  if (!(dist > 0) || !(heightPx > 0) || !(fovYRad > 0)) return 0;
  return dist * Math.tan((px * fovYRad) / heightPx);
}
/** Limb darkening as the rig's three coefficients: I(μ) = a + bμ − cμ² with I(1) = 1. */
export function limbCoeffs(kind: StarKind): [number, number, number] {
  const L = STAR_LOOK[kind];
  return [L.limbA, 1 - L.limbA + L.limbC, L.limbC];
}
export function limbAt(kind: StarKind, rOverR: number): number {
  const [a, b, c] = limbCoeffs(kind), mu = Math.sqrt(Math.max(0, 1 - rOverR * rOverR));
  return a + b * mu - c * mu * mu;
}
/** The spot field's spread: set so that the Sun's own setting (0.52 / 0.62) is a 2 % cover. */
const SPOT_SIGMA = 0.57 / (Math.log(0.98 / 0.02) / 1.702);
/**
 * The spot field's two thresholds for a share of the disc under spots. The field is gradient noise (roughly normal,
 * σ ≈ 0.27): a spot is where it exceeds `lo`, fully dark by `hi`. 2 % is the Sun's own setting (0.52 / 0.62).
 */
export function spotThresholds(cover: number): [number, number] {
  if (!(cover > 0)) return [9, 9.1];
  const p = clamp(cover, 1e-4, 0.9);
  // the normal quantile (Acklam's rational fit is more than this needs: a logistic stand-in holds 1 % over 0.01..0.9)
  const z = Math.log((1 - p) / p) / 1.702;
  const lo = clamp(z * SPOT_SIGMA - 0.05, -0.6, 0.7);
  return [lo, lo + 0.1];
}

// ── schedules: things that happen now and then, the same for every client ───────────────────────────────────────
/**
 * The last event of a deterministic schedule: one event per slot of `meanS` seconds, placed `jitter` (0..1) of a slot
 * into it by the slot's hash. Returns the seconds since it began and its index (its own random draws key on that).
 */
export function lastEvent(seed: number, tag: string, nowS: number, meanS: number, jitter = 0.6): { since: number; index: number } {
  let k = Math.floor(nowS / meanS);
  let t = (k + starHash01(`${seed}:${tag}:${k}`) * jitter) * meanS;
  if (t > nowS) { k -= 1; t = (k + starHash01(`${seed}:${tag}:${k}`) * jitter) * meanS; }
  return { since: nowS - t, index: k };
}

/** Davenport's flare (2014): `x` = time since the peak in half-widths. A quartic rise over one half-width, then two exponentials. */
export function flareShape(x: number): number {
  if (x < -1) return 0;
  if (x <= 0) return clamp(1 + 1.941 * x - 0.175 * x * x - 2.246 * x * x * x - 1.125 * x * x * x * x, 0, 1);
  return 0.689 * Math.exp(-1.6 * x) + 0.303 * Math.exp(-0.2783 * x);
}

export interface FlareNow { amp: number; lon: number; lat: number }
/**
 * The flares burning at `nowS` on a star with `perMin` flares a minute: one candidate per 20 s slot, a power-law size
 * (many small, few large), a half-width of 2–5 s. The brightest one's place on the star rides out with the sum.
 */
export function flaresAt(seed: number, nowS: number, perMin: number, big = 1): FlareNow {
  const SLOT = 20, p = clamp((perMin * SLOT) / 60, 0, 1);
  let amp = 0, best = 0, lon = 0, lat = 0;
  if (p <= 0) return { amp: 0, lon: 0, lat: 0 };
  const k0 = Math.floor(nowS / SLOT);
  for (let k = k0 - 4; k <= k0 + 1; k++) { // WHY k0 + 1: a flare peaks ≥ 2 s into its slot and rises for up to 5 s before — unread, its light stepped by up to 0.34 as the clock crossed the boundary
    const key = `${seed}:fl:${k}`;
    if (starHash01(`${key}:on`) >= p) continue;
    const half = 2 + starHash01(`${key}:w`) * 3;
    const peak = (k + 0.1 + starHash01(`${key}:t`) * 0.8) * SLOT;
    const u = starHash01(`${key}:a`);
    const a = big * (0.12 + 1.1 * u * u * u) * flareShape((nowS - peak) / half);
    if (a > best) { best = a; lon = starHash01(`${key}:lo`) * TAU; lat = (starHash01(`${key}:la`) - 0.5) * 1.6; }
    amp += a;
  }
  return { amp, lon, lat };
}

/** A smooth 0..1 wander with no period the eye can find: three sines of unrelated periods about `baseS`. */
export function wander(seed: number, tag: string, nowS: number, baseS: number): number {
  const a = starHash01(`${seed}:${tag}:a`) * TAU, b = starHash01(`${seed}:${tag}:b`) * TAU, c = starHash01(`${seed}:${tag}:c`) * TAU;
  const v = Math.sin((nowS / baseS) * TAU + a) * 0.5 + Math.sin((nowS / (baseS * 1.618)) * TAU + b) * 0.3 + Math.sin((nowS / (baseS * 0.3819)) * TAU + c) * 0.2;
  return clamp(0.5 + 0.5 * v, 0, 1);
}

/** A pulsator's cycle, 0..1: `rise` of it climbing, the rest falling (cosine ramps: periodic, continuous, no kink). */
export function sawCycle(phase: number, rise: number): number {
  const p = frac(phase);
  return p < rise ? 0.5 - 0.5 * Math.cos((Math.PI * p) / rise) : 0.5 + 0.5 * Math.cos((Math.PI * (p - rise)) / (1 - rise));
}

/** A Cepheid's period in seconds of play (minutes, as the brief asks: a cycle a pilot can watch from the station). */
export const cepheidPeriodS = (seed: number): number => 200 + (starHash(`${seed}:cep`) % 181);
/** A Mira's: a slow swing over a quarter of an hour or more. */
export const miraPeriodS = (seed: number): number => 900 + (starHash(`${seed}:mira`) % 541);
/** A pulsar's visible turn: slow enough to read, never a strobe (two beams: one passes every half turn). */
export const pulsarSpinS = (seed: number): number => 7 + (starHash(`${seed}:spin`) % 4);
/** The seconds between a magnetar's giant flares (rare), and how long one's tail lasts. */
export const MAGNETAR_EVERY_S = 1500;
export const MAGNETAR_TAIL_S = 75;

export interface StarPulse {
  /** × the star's steady light (its visible flux). */
  light: number;
  /** Its temperature now (K) — the colour. */
  kelvin: number;
  /** × its drawn radius. */
  radius: number;
  /** × its disc emission (surface brightness). */
  emission: number;
  /** A flare's white patch: its strength (0 = none) and where on the star (rad). */
  flare: FlareNow;
  /** The surface's turn (rad) and, for a pulsar, its beams'. */
  spin: number;
  /** The cycle's phase 0..1 (pulsators) or the slow state 0..1 (an LBV: 0 hot and small, 1 cool and large). */
  phase: number;
  /** Seconds since the kind's rare event began (a magnetar's giant flare); Infinity = none in reach. */
  burstS: number;
}

/**
 * What a star of this kind is doing at wall-clock `nowS`. Deterministic in (kind, seed, nowS).
 * `light` is the VISIBLE flux relative to the kind's mean: emission × radius² holds it (a pulsator's disc is drawn
 * with the surface brightness its light and size imply).
 */
export function starPulse(kind: StarKind, seed: number, nowS: number): StarPulse {
  const L = STAR_LOOK[kind];
  const out: StarPulse = { light: 1, kelvin: L.kelvin, radius: 1, emission: 1, flare: { amp: 0, lon: 0, lat: 0 }, spin: L.spinS > 0 ? frac(nowS / L.spinS + starHash01(`${seed}:spin0`)) * TAU : 0, phase: 0, burstS: Infinity };
  if (!Number.isFinite(nowS)) return out;
  switch (L.vary) {
    case 'cepheid': {
      const P = cepheidPeriodS(seed), ph = frac(nowS / P + starHash01(`${seed}:ph`));
      const s = sawCycle(ph, 0.28);                     // 0 at minimum light, 1 at maximum
      out.phase = ph;
      out.light = lerp(0.66, 1.34, s);                  // 0.77 mag peak to peak (δ Cephei: 0.9 in V)
      out.kelvin = lerp(5450, 6550, s);                 // hotter at maximum
      out.radius = 1 + 0.05 * Math.sin((ph - 0.28) * TAU); // largest a quarter cycle after maximum light
      break;
    }
    case 'mira': {
      const P = miraPeriodS(seed), ph = frac(nowS / P + starHash01(`${seed}:ph`));
      const s = sawCycle(ph, 0.4);
      out.phase = ph;
      out.light = lerp(0.42, 1.36, s);                  // 1.3 mag here (the real swing is 2.5–10: the oxide veil closes)
      out.kelvin = lerp(2550, 3300, s);
      out.radius = lerp(1.08, 0.94, s);                 // swollen at minimum, compact at maximum
      break;
    }
    case 'semiregular': {
      // two unrelated beats (Betelgeuse: ~400 d on a ~2,100 d long secondary period) — here minutes on tens of minutes
      const w = wander(seed, 'sr', nowS, 620), w2 = wander(seed, 'lsp', nowS, 2900);
      const s = clamp(0.62 * w + 0.38 * w2, 0, 1);
      out.phase = s;
      out.light = lerp(0.78, 1.18, s);
      out.kelvin = L.kelvin + lerp(-140, 120, s);
      out.radius = lerp(1.03, 0.97, s);
      break;
    }
    case 'sdor': {
      // 0 = quiescence (hot, small), 1 = outburst (cool, large, visually brighter at the same total light)
      const s = wander(seed, 'sdor', nowS, 2300);
      const e = s * s * (3 - 2 * s);
      out.phase = e;
      out.kelvin = lerp(24000, 8600, e);
      out.radius = lerp(0.86, 1.14, e);
      out.light = lerp(0.72, 1.22, e);
      break;
    }
    case 'flare':
    case 'active': {
      out.flare = flaresAt(seed, nowS, L.vary === 'flare' ? 0.8 : 0.08, L.vary === 'flare' ? 1 : 0.5);
      out.light = 1 + out.flare.amp;
      break;
    }
    case 'ttauri': {
      // spots turning with the star, accretion that comes and goes, and flares
      const rot = 0.08 * Math.sin(out.spin + 1.3), acc = wander(seed, 'acc', nowS, 140);
      out.flare = flaresAt(seed, nowS, 0.5, 0.8);
      out.phase = acc;
      out.light = clamp(0.86 + rot + 0.3 * acc + out.flare.amp, 0.5, 2.4);
      out.kelvin = L.kelvin + 500 * acc;                // veiling: the hot spots' blue continuum
      break;
    }
    case 'herbig': {
      // UX Orionis dips: a clump of the disc crosses the star now and then — dimmer and redder for a minute
      const ev = lastEvent(seed, 'dip', nowS, 420, 0.7), u = ev.since / (40 + starHash01(`${seed}:dipw:${ev.index}`) * 50);
      const dip = u < 1 ? Math.sin(Math.PI * u) ** 2 * (0.25 + 0.35 * starHash01(`${seed}:dipd:${ev.index}`)) : 0;
      out.phase = dip;
      out.light = 1 - dip;
      out.kelvin = L.kelvin - 2600 * dip;
      break;
    }
    case 'protostar': {
      const acc = wander(seed, 'acc', nowS, 260);
      out.phase = acc;
      out.light = lerp(0.8, 1.3, acc);
      out.kelvin = L.kelvin + 300 * acc;
      break;
    }
    case 'be': {
      out.phase = wander(seed, 'disc', nowS, 1500);      // the disc builds and thins (star_view reads it)
      break;
    }
    case 'pulsar': {
      out.spin = frac(nowS / pulsarSpinS(seed) + starHash01(`${seed}:spin0`)) * TAU;
      break;
    }
    case 'magnetar': {
      const spin = pulsarSpinS(seed);
      out.spin = frac(nowS / spin + starHash01(`${seed}:spin0`)) * TAU;
      const ev = lastEvent(seed, 'giant', nowS, MAGNETAR_EVERY_S, 0.8);
      out.burstS = ev.since;
      if (ev.since < MAGNETAR_TAIL_S) {
        // a hard spike (well under a second in nature — here one long enough to see), then a tail that rides the spin
        const spike = ev.since < 0.35 ? ev.since / 0.35 : Math.exp(-(ev.since - 0.35) / 1.1);
        const tail = Math.exp(-ev.since / 22) * (0.7 + 0.3 * Math.cos(out.spin)) * (ev.since > 0.35 ? 1 : 0);
        out.light = 1 + 3.2 * spike + 0.9 * tail;
        out.flare = { amp: 2.5 * spike + 0.8 * tail, lon: 0, lat: 0 };
      }
      break;
    }
    default: break;
  }
  // the disc's surface brightness that this light and this size imply
  out.radius = clamp(out.radius, 0.7, 1.2);
  out.light = clamp(out.light, 0.05, 6);
  const quiet = out.light - out.flare.amp;             // a flare is a patch, not the whole photosphere
  out.emission = clamp(Math.max(0.05, quiet) / (out.radius * out.radius), 0.05, 4);
  return out;
}

// ── pairs: two stars inside one footprint ───────────────────────────────────────────────────────────────────────
/** The Roche lobe's volume radius ÷ the separation, for mass ratio q = m(this) / m(other) (Eggleton 1983, 1 %). */
export function rocheLobe(q: number): number {
  const q3 = Math.cbrt(q), q23 = q3 * q3;
  return (0.49 * q23) / (0.6 * q23 + Math.log(1 + q3));
}

export interface PairSpec {
  config: SystemConfig;
  a: StarKind; b: StarKind;
  /** Drawn radii (m). */
  ra: number; rb: number;
  /** Semi-major axis of the RELATIVE orbit (m) and its eccentricity. */
  sepM: number; ecc: number;
  /** The primary's share of the mass (it sits (1 − share) × the separation from the barycentre, which is the origin). */
  share: number;
  /** One orbit in seconds of wall clock; the mean anomaly at wall-clock 0 (turns). */
  periodS: number; phase0: number;
  /** The orbit's plane: inclination to the system plane and node (rad). Close to the plane, so ships on the lanes see eclipses. */
  inc: number; node: number;
  /** The accretor's disc (semidetached, X-ray, symbiotic) or the Be star's: outer radius (m), 0 = none. */
  discM: number;
}

/** A contact pair's lobes as circles: each reaches this far past the inner Lagrange point, so the two overlap in a neck. */
export const CONTACT_OVERFILL = 1.12;
/** Relative sizes two main-sequence stars keep next to each other (a pair is drawn smaller than either alone: both must fit). */
const PAIR_SIZE: Readonly<Partial<Record<StarKind, number>>> = { m_dwarf: 0.5, k_dwarf: 0.78, g_dwarf: 1, f_dwarf: 1.2, a_star: 1.5, t_tauri: 1, ob_star: 1.5 };

/**
 * How a system's two inner stars are arranged. Everything stays inside `footprint × STELLAR_REACH`: the barycentre is
 * the origin, each star's far edge (and an accretor's disc) is checked against the reach, and the pair is scaled to fit.
 */
export function pairSpec(config: SystemConfig, a: StarKind, b: StarKind, footprintM: number, seed: number): PairSpec | null {
  const F = footprintM, reach = F * STELLAR_REACH * 0.96;
  const ma = STAR_LOOK[a].mass, mb = STAR_LOOK[b].mass, share = ma / (ma + mb);
  const h = (t: string): number => starHash01(`${seed}:pair:${t}`);
  const base = { config, a, b, share, phase0: h('ph'), inc: (h('inc') - 0.5) * 0.14, node: h('node') * TAU, ecc: 0, discM: 0 };
  switch (config) {
    case 'detached_binary': case 'hierarchical_triple': case 'young_binary': {
      const sa = PAIR_SIZE[a] ?? 1, sb = PAIR_SIZE[b] ?? 1, young = config === 'young_binary';
      const gap = young ? 2.1 : 1.6;                               // young pairs keep room for their own discs …
      const disc = young ? 0.33 : 0;                               // … each cut off by the other star's tide at a third of the way
      // radii s·sa, s·sb; separation k·s; each far edge (the star's, or its disc's) inside the reach
      const k = gap * (sa + sb);
      const s = Math.min(reach / (k * (1 - share) + Math.max(sa, k * disc)), reach / (k * share + Math.max(sb, k * disc)));
      const ra = s * sa, rb = s * sb, sepM = k * s;
      return { ...base, ra, rb, sepM, periodS: Math.round((young ? 420 : 260) + h('P') * 160), discM: disc * sepM };
    }
    case 'contact_binary': {
      // two lobes that overlap: the neck. W UMa pairs are the fastest binaries there are.
      // each lobe reaches a little past the inner Lagrange point (0.5 of the way for equal masses)
      const xa = clamp(0.5 + 0.227 * Math.log10(ma / mb), 0.3, 0.7), la = xa * CONTACT_OVERFILL, lb = (1 - xa) * CONTACT_OVERFILL;
      const sepM = Math.min(reach / Math.max((1 - share) + la, share + lb), F * 0.8);
      return { ...base, ra: sepM * la, rb: sepM * lb, sepM, periodS: Math.round(96 + h('P') * 40), inc: (h('inc') - 0.5) * 0.3 };
    }
    case 'semidetached': case 'xray_binary': {
      // the donor (a) fills its lobe; the accretor (b) wears a disc inside its own
      const q = ma / mb, la = rocheLobe(q), lb = rocheLobe(1 / q);
      const sepM = reach / Math.max((1 - share) + la, share + lb * 0.72);
      return { ...base, ra: sepM * la, rb: STAR_LOOK[b].sizeM ?? F * STAR_LOOK[b].size, sepM, discM: sepM * lb * 0.62, periodS: Math.round(150 + h('P') * 80) };
    }
    case 'symbiotic': {
      // a giant that does NOT fill its lobe: its wind feeds the dwarf
      const ra = F * 0.72, sepM = Math.min((reach - ra) / (1 - share), (reach - F * 0.1) / share, F * 1.3);
      return { ...base, ra, rb: STAR_LOOK[b].sizeM ?? F * 0.03, sepM, discM: F * 0.085, periodS: Math.round(520 + h('P') * 200) };
    }
    case 'be_xray': {
      // an eccentric orbit: the neutron star dives through the Be star's disc at periastron
      const ra = F * 0.42, ecc = 0.36, sepM = (reach - F * 0.02) / ((1 + ecc) * share);
      const discM = Math.min(reach - sepM * (1 + ecc) * (1 - share), sepM * (1 - ecc) * 1.45);
      return { ...base, ra, rb: STAR_LOOK[b].sizeM ?? 1_200, sepM, ecc, discM, periodS: Math.round(300 + h('P') * 120), inc: (h('inc') - 0.5) * 0.3 };
    }
    case 'colliding_wind': {
      const ra = starRadiusM(a, F) * 0.9, rb = F * 0.36, sepM = Math.min((reach - rb) / share, (reach - ra) / (1 - share), F * 1.05);
      // (tipped well out of the system's plane: its dust spiral lies in the orbit, and a pinwheel seen from the lanes
      //  edge-on is a line — eclipses are not this pair's show)
      return { ...base, ra, rb, sepM, ecc: 0, periodS: Math.round(540 + h('P') * 180), inc: 0.6 + h('inc') * 0.3 };
    }
    default: return null;
  }
}

/** Where the two stars are at wall-clock `nowS` (sector metres about the barycentre at the origin). */
export function pairAt(p: PairSpec, nowS: number, outA: V3, outB: V3): { anomaly: number; dist: number } {
  let M = frac(nowS / p.periodS + p.phase0) * TAU;
  let E = M;
  if (p.ecc > 0) { for (let i = 0; i < 8; i++) E -= (E - p.ecc * Math.sin(E) - M) / (1 - p.ecc * Math.cos(E)); }
  const px = p.sepM * (Math.cos(E) - p.ecc), pz = p.sepM * Math.sqrt(1 - p.ecc * p.ecc) * Math.sin(E); // b relative to a
  const ci = Math.cos(p.inc), si = Math.sin(p.inc), cn = Math.cos(p.node), sn = Math.sin(p.node);
  const y = pz * si, z2 = pz * ci;
  const rx = px * cn - z2 * sn, rz = px * sn + z2 * cn;
  const wa = -(1 - p.share), wb = p.share;
  outA[0] = rx * wa; outA[1] = y * wa; outA[2] = rz * wa;
  outB[0] = rx * wb; outB[1] = y * wb; outB[2] = rz * wb;
  M = Math.atan2(pz, px);                             // the true anomaly (b about a, in the orbit's plane)
  return { anomaly: M, dist: Math.hypot(px, pz) };
}

/** The farthest any part of the pair (a star's edge, the accretor's disc) reaches from the origin, over a whole orbit. */
export function pairReachM(p: PairSpec): number {
  const far = p.sepM * (1 + p.ecc);
  const discA = p.config === 'be_xray' || p.config === 'young_binary' ? p.discM : 0;
  const discB = p.config === 'semidetached' || p.config === 'xray_binary' || p.config === 'symbiotic' || p.config === 'young_binary' ? p.discM : 0;
  return Math.max(far * (1 - p.share) + Math.max(p.ra, discA), far * p.share + Math.max(p.rb, discB));
}

export interface PairEclipse {
  /** The share of each disc in view (1 = all of it). */
  visA: number; visB: number;
  /** True when the primary is the nearer of the two. */
  aInFront: boolean;
  /** Their separation on the sky and their angular radii (rad). */
  sep: number; angA: number; angB: number;
}
/** The mutual eclipse of two stars as a viewer sees it: the nearer disc covers the farther (space_look.discOverlapArea). */
export function pairEclipse(view: { x: number; y: number; z: number }, A: V3, ra: number, B: V3, rb: number): PairEclipse {
  const ax = A[0] - view.x, ay = A[1] - view.y, az = A[2] - view.z, bx = B[0] - view.x, by = B[1] - view.y, bz = B[2] - view.z;
  const da = Math.hypot(ax, ay, az) || 1, db = Math.hypot(bx, by, bz) || 1;
  const angA = da <= ra ? Math.PI / 2 : Math.asin(ra / da), angB = db <= rb ? Math.PI / 2 : Math.asin(rb / db);
  const sep = Math.acos(clamp((ax * bx + ay * by + az * bz) / (da * db), -1, 1));
  const ov = discOverlapArea(angA, angB, sep);
  const aInFront = da <= db;
  const out: PairEclipse = { visA: 1, visB: 1, aInFront, sep, angA, angB };
  if (ov > 0) {
    if (aInFront) out.visB = clamp(1 - ov / (Math.PI * angB * angB), 0, 1);
    else out.visA = clamp(1 - ov / (Math.PI * angA * angA), 0, 1);
  }
  return out;
}

/** A light as the key light counts it: its colour (linear), its visible flux (emission × drawn area, any unit) and how much of it is in view. */
export interface LightPart { rgb: Rgb; flux: number; vis: number }
/**
 * Several sources as ONE key light: the colour is the flux-weighted mix of what is in view, the scale is the share of
 * the system's FULL light still in view (an eclipse dims by exactly the covered flux) × `base`, floored for play.
 */
export function mixKeyLight(parts: ReadonlyArray<LightPart>, base: number, floor = KEY_LIGHT_FLOOR, cap = KEY_LIGHT_BURST): { rgb: Rgb; scale: number; share: number } {
  let full = 0, seen = 0; const c: Rgb = [0, 0, 0];
  for (const p of parts) {
    const f = Math.max(0, p.flux), v = clamp(p.vis, 0, 1);
    full += f; seen += f * v;
    c[0] += p.rgb[0] * f * v; c[1] += p.rgb[1] * f * v; c[2] += p.rgb[2] * f * v;
  }
  const share = full > 0 ? seen / full : 1;
  const rgb: Rgb = seen > 0 ? norm1(c) : [1, 1, 1];
  return { rgb, scale: clamp(base * share, floor, cap), share };
}

// ── events on a white dwarf (EV-01, EV-02) ──────────────────────────────────────────────────────────────────────
/** A dwarf nova's cycle: the disc flips to its hot state every several minutes (SS Cygni: every ~50 days for 1–2 weeks). */
export const DWARF_NOVA = { everyS: 420, riseS: 10, holdS: 38, fallS: 46, gain: 7 } as const;
/** × the disc's light, `since` seconds after an outburst began: a quick rise, a plateau, a slower fall. */
export function dwarfNovaLight(since: number): number {
  const d = DWARF_NOVA;
  if (!(since >= 0)) return 1;
  let s = 0;
  if (since < d.riseS) { const u = since / d.riseS; s = u * u * (3 - 2 * u); }
  else if (since < d.riseS + d.holdS) s = 1 - 0.12 * ((since - d.riseS) / d.holdS);
  else if (since < d.riseS + d.holdS + d.fallS) { const u = (since - d.riseS - d.holdS) / d.fallS; s = 0.88 * (1 - u * u * (3 - 2 * u)); }
  return 1 + (d.gain - 1) * s;
}
/** A nova: seconds between eruptions (a classical nova is rare; a symbiotic's recurs), and how long its shell is seen. */
export const NOVA = { everyS: 2700, recurrentS: 1900, riseS: 2.5, t2S: 34, shellS: 420 } as const;
/**
 * A nova's light, `since` seconds after the runaway: up in seconds, down two magnitudes in t₂, then a long tail
 * (real novae: up 8–15 mag in a day or so, t₂ from days to months). Returns × the white dwarf's own light, 1 = quiet.
 */
export function novaLight(since: number, peak = 60): number {
  if (!(since >= 0) || since > NOVA.shellS) return 1;
  if (since < NOVA.riseS) { const u = since / NOVA.riseS; return 1 + (peak - 1) * u * u; }
  const mags = (2 * (since - NOVA.riseS)) / NOVA.t2S;       // 2 mag per t₂ early on …
  const m = mags < 3 ? mags : 3 + (mags - 3) * 0.45;          // … then the slow tail
  return 1 + (peak - 1) * Math.pow(10, -0.4 * m);
}
/** The ejected shell's radius (m) for a footprint, `since` seconds on: it leaves fast and coasts (kept inside the reach's haze). */
export function novaShellM(since: number, footprintM: number): number {
  const u = clamp(since / NOVA.shellS, 0, 1);
  return footprintM * (0.12 + 2.3 * (1 - Math.pow(1 - u, 1.6)));
}
